import { useSyncExternalStore } from 'react';
import { DEV_BUILD_ID, hasNewAppVersion, parseVersionPayload } from '../utils/appVersion';
const POLL_MS = 10 * 1000;
// O fim do deploy espera esta consulta; sem limite, uma requisição travada prende o banner de deploy.
export const VERSION_FETCH_TIMEOUT_MS = 5 * 1000;
const VERSION_URL = '/version.json';
// Store único: banners de deploy e de nova versão compartilham o mesmo poll e o mesmo estado.
const listeners = new Set();
let hasUpdate = false;
let intervalId = null;
let inFlight = null;
function readCurrentBuildId() {
    return process.env.REACT_APP_BUILD_ID || DEV_BUILD_ID;
}
function isVersionCheckDisabled() {
    return process.env.NODE_ENV === 'development' || readCurrentBuildId() === DEV_BUILD_ID;
}
function stopPolling() {
    if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
    }
}
function markUpdated() {
    if (hasUpdate)
        return;
    hasUpdate = true;
    stopPolling();
    listeners.forEach(listener => listener());
}
async function fetchVersion() {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS);
    try {
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
            cache: 'no-store',
            credentials: 'same-origin',
            signal: controller.signal,
        });
        if (!response.ok)
            return hasUpdate;
        const remote = parseVersionPayload(await response.json());
        if (hasNewAppVersion(readCurrentBuildId(), remote))
            markUpdated();
    }
    catch {
        // silêncio (inclui timeout): poll seguinte tenta de novo
    }
    finally {
        window.clearTimeout(timeoutId);
    }
    return hasUpdate;
}
export function checkAppVersionNow() {
    if (isVersionCheckDisabled())
        return Promise.resolve(false);
    if (hasUpdate)
        return Promise.resolve(true);
    if (!inFlight) {
        inFlight = fetchVersion().finally(() => {
            inFlight = null;
        });
    }
    return inFlight;
}
function startPolling() {
    if (hasUpdate)
        return;
    void checkAppVersionNow();
    stopPolling();
    intervalId = window.setInterval(() => void checkAppVersionNow(), POLL_MS);
}
function onVisible() {
    if (document.visibilityState === 'visible')
        startPolling();
    else
        stopPolling();
}
function subscribe(listener) {
    listeners.add(listener);
    if (listeners.size === 1 && !isVersionCheckDisabled()) {
        if (document.visibilityState === 'visible')
            startPolling();
        window.addEventListener('focus', onVisible);
        document.addEventListener('visibilitychange', onVisible);
    }
    return () => {
        listeners.delete(listener);
        if (listeners.size > 0)
            return;
        stopPolling();
        window.removeEventListener('focus', onVisible);
        document.removeEventListener('visibilitychange', onVisible);
    };
}
function getSnapshot() {
    return hasUpdate;
}
export function resetAppVersionCheckForTests() {
    stopPolling();
    listeners.clear();
    hasUpdate = false;
    inFlight = null;
    window.removeEventListener('focus', onVisible);
    document.removeEventListener('visibilitychange', onVisible);
}
export function useAppVersionCheck() {
    return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
